import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { ActionsMenu } from '~/shared/components/ActionsMenu';
import Button from '~/shared/components/ButtonsV2';
import { usePermission } from '~/shared/hooks/usePermission';
import Tag from '~/shared/components/Tag';
import Typography from '~/shared/components/Typography';
import LinkIcon from '~/shared/assets/icons/link-gray.svg';
import CheckIcon from '~/shared/assets/icons/check.svg';
import CancelIcon from '~/shared/assets/icons/cancel.svg';
import ExceptionIcon from '~/shared/assets/icons/exception-2.svg';
import { formatBytesWithValueAndUnit } from '~/shared/utils/formatNumber';

import { buildVolumePageUrl } from '~/fleet-manager/components/FleetManagerPage/helpers';
import { isSwitchedOn } from '~/shared/utils/volumeHelpers';

import type { VolumeSummary } from '../hooks/useAutoscaleRulesQuery';

interface VolumeInfoCardProps {
  volume: VolumeSummary;
  onCreateException?: (volumeId: string) => void;
  onRemoveException?: (volumeId: string, volumeName?: string) => void;
  volumeType?: 'matching' | 'exception' | 'potential';
}

export default function VolumeInfoCard({
  volume,
  onCreateException,
  onRemoveException,
  volumeType,
}: VolumeInfoCardProps) {
  const navigate = useNavigate();
  const canWrite = usePermission('datafy.write.autoscaling.rules');
  const volumeId = volume.volumeId ?? 'N/A';
  const utilization = volume.utilization;

  const { value, unit } = formatBytesWithValueAndUnit(volume.diskSize || 0, 1);
  const isAutoscalingOn = isSwitchedOn(volume);

  const actionsMenuItems = useMemo(
    () => [
      {
        id: 'create-exception',
        label: 'Create exception from rules',
        icon: <ExceptionIcon />,
        disabled: !canWrite,
        onClick: () => onCreateException?.(volumeId),
      },
    ],
    [canWrite, onCreateException, volumeId]
  );

  return (
    <div className="flex flex-col gap-2  p-5 h-fit overflow-y-auto border-gray-300 border rounded-lg">
      <div className="flex justify-between items-center">
        <Typography variant="p2-semi" color="text-gray-500" fontWeight={500} className="">
          {volumeId}
        </Typography>
        <div className="flex">
          <Button
            variant="text"
            size="sm"
            className="group w-7 !h-7 aspect-square"
            icon={
              <LinkIcon className="size-4 text-gray-600 group-hover:stroke-primary-500 group-hover:text-primary-500" />
            }
            onClick={() => navigate(buildVolumePageUrl(volumeId, volume.accountId))}
          />

          {volumeType !== 'exception' && onCreateException ? (
            <ActionsMenu
              items={actionsMenuItems}
              aria-label={`Actions for volume ${volumeId}`}
              disabled={!canWrite}
              menuWidth={262}
            />
          ) : null}
        </div>
      </div>
      <div className="min-h-[28px]">
        {volume.instanceId && <Tag variant="instance" size="sm" text={volume.instanceId} />}
      </div>

      <div className="flex flex-col gap-1.5">
        <div className="flex divide-x-2 gap-2 py-1 divide-gray-400 ">
          <div className="flex gap-1 items-center pr-3">
            <Typography variant="p3-reg" color="text-gray-800" fontWeight={400} className="">
              Autoscaling
            </Typography>
            {isAutoscalingOn ? (
              <CheckIcon className="size-[14px] mx-1 text-green stroke-0.5" />
            ) : (
              <CancelIcon className="size-[20px] text-red" />
            )}
          </div>
          {(utilization ?? 0) > 0 && (
            <Typography variant="p3-reg" color="text-gray-800" fontWeight={400} className="pr-3">
              {Math.round(utilization!)}% utilized
            </Typography>
          )}
          <Typography variant="p3-reg" color="text-gray-800" fontWeight={400} className="pr-3">
            {value} {unit}
          </Typography>
        </div>
      </div>

      {volumeType === 'exception' && (
        <div className="flex items-center">
          <ExceptionIcon className="size-6 mr-1 text-gray-600" />
          <Typography variant="p3-reg" color="text-gray-800" fontWeight={400} className="">
            Manual override
          </Typography>
          <Button
            variant="third"
            size="sm"
            className="ml-auto"
            onClick={() => onRemoveException?.(volumeId, volume.volumeId)}
            disabled={!canWrite}
          >
            <Typography variant="buttons-m" color="text-primary-900" fontWeight={600} className="">
              Remove Exception
            </Typography>
          </Button>
        </div>
      )}
    </div>
  );
}
